import { FC, useState, useCallback, useEffect, ReactNode } from 'react';
import { useFragment, graphql } from 'react-relay';
import { useAuctionState } from 'dibs-auctions/exports/hooks';
import classnames from 'classnames';

// components
import { FormattedMessage } from 'dibs-react-intl';
import { CountdownTimer, withinWarningThreshold } from 'dibs-elements/exports/CountdownTimer';
import { Link } from 'dibs-elements/exports/Link';
import Circle from 'dibs-icons/exports/legacy/Circle';

// types
import { PdpSharedAuctionCountdown_item$key as Item } from './__generated__/PdpSharedAuctionCountdown_item.graphql';

// styles
import dibsCss from 'dibs-css';
import styles from './PdpSharedAuctionCountdown.scss';
import { useSharedAuctionContext } from '../PdpSharedAuctionContext/PdpSharedAuctionContext';

const WARNING_THRESHOLD = 86399000; // 1000 * 60 * 60 * 24 - 1000 -> 23h 59m 59s

const countdownCompleteActive = (
    <FormattedMessage
        id="PdpSharedAuctionCountdown.completeActive"
        defaultMessage="Auction ended"
    />
);

const countdownCompletePreview = (
    <FormattedMessage
        id="PdpSharedAuctionCountdown.completePreview"
        defaultMessage="Bidding About To Open"
    />
);

const biddingClosed = (
    <div className={classnames(dibsCss.sassyFontBodyMediumHeavy, dibsCss.mtSmall)}>
        <FormattedMessage
            id="PdpSharedAuctionCountdown.biddingClosed"
            defaultMessage="Bidding Closed"
        />
    </div>
);

type CountdownBoxProps = {
    children: ReactNode;
    isAuctionSiderailRedesignVariant?: boolean | null;
    isAuctionFinished?: boolean;
};

const CountdownBox: FC<CountdownBoxProps> = ({
    children,
    isAuctionSiderailRedesignVariant,
    isAuctionFinished = false,
}) => (
    <div
        className={classnames({
            [styles.countdownBox]: !isAuctionSiderailRedesignVariant,
            [styles.countdownEnded]: isAuctionFinished,
        })}
    >
        {children}
    </div>
);

type CountdownMessageProps = {
    isPreviewAuction?: boolean;
    isAuctionSiderailRedesignVariant: boolean | null;
    targetDate: string;
    onCountdownFinished: () => void;
};

const CountdownMessage: FC<CountdownMessageProps> = ({
    isPreviewAuction,
    isAuctionSiderailRedesignVariant,
    targetDate,
    onCountdownFinished,
}) => {
    const withinThreshold = withinWarningThreshold(targetDate, WARNING_THRESHOLD);

    return isAuctionSiderailRedesignVariant ? (
        <div className={styles.testVariantContainer}>
            <span
                className={classnames({
                    [styles.warnEnding]: withinThreshold,
                })}
            >
                <Circle className={styles.circleIcon} />
            </span>
            <span>
                <FormattedMessage
                    id="SharedAuctionCountdown.auctionCountdownMessageTestVariant"
                    defaultMessage={`{isPreviewAuction, select, true {Live in {countdown}} other {Ends in {countdown}}}`}
                    values={{
                        countdown: (
                            <CountdownTimer
                                shouldReturnText
                                countdownText
                                targetDate={targetDate}
                                onCountdownFinished={onCountdownFinished}
                                loadOnFirstRender
                            />
                        ),
                        isPreviewAuction,
                    }}
                />
            </span>
        </div>
    ) : (
        <span
            className={classnames({
                [styles.warnEnding]: withinThreshold,
            })}
        >
            <FormattedMessage
                id="SharedAuctionCountdown.auctionCountdownMessage"
                defaultMessage={`{isPreviewAuction, select, true {Auction Live in {countdown}} other {Auction Ends in {countdown}}}`}
                values={{
                    countdown: (
                        <CountdownTimer
                            shouldReturnText
                            countdownText
                            targetDate={targetDate}
                            warningThresholdMS={WARNING_THRESHOLD}
                            onCountdownFinished={onCountdownFinished}
                            loadOnFirstRender
                        />
                    ),
                    isPreviewAuction,
                }}
            />
        </span>
    );
};

const itemFragment = graphql`
    fragment PdpSharedAuctionCountdown_item on Item {
        ...useAuctionState_item @arguments(page: pdp)
        relevantAuctionLot(page: pdp) {
            startDate
            endDate
        }
    }
`;

type Props = {
    item: Item;
    isAuctionSiderailRedesignVariant?: boolean | null;
};

export const PdpSharedAuctionCountdown: FC<Props> = ({
    item: itemRef,
    isAuctionSiderailRedesignVariant = false,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const { isPreviewAuction, isActiveAuction, isTransitionAuction } = useAuctionState({ item });
    const { setIsBiddingDisabled, dispatchUpdateAuctionItem, shouldDisplayCountdown } =
        useSharedAuctionContext();
    const { startDate, endDate } = item?.relevantAuctionLot || {};
    const [isCountdownFinished, setIsCountdownFinished] = useState<boolean>(
        isTransitionAuction || false
    );
    const showActiveCountdown = !isCountdownFinished && !isTransitionAuction;

    /* When an auction goes from active to preview and there are reauctions left,
       the endDate and startDate values are updated, which triggers this useEffect.
       - here the timer becomes `Auction Live in {countdown}
       But when a preview auction finishes, the endDate and startDate are not updated,
       so we instead hook into isActiveAuction to trigger a rerender and display the correct copy.
       - here the timer becomes `Auction Ends in {countdown}
    */
    useEffect(() => {
        setIsCountdownFinished(false);
    }, [endDate, startDate, isActiveAuction]);

    const onCountdownFinished = useCallback(() => {
        dispatchUpdateAuctionItem();
        if (isActiveAuction) {
            setIsBiddingDisabled(true);
        }
        setIsCountdownFinished(true);
    }, [isActiveAuction, setIsBiddingDisabled, dispatchUpdateAuctionItem]);

    if (!shouldDisplayCountdown) {
        return null;
    }

    const auctionEnded = (
        <>
            <span data-tn="pdp-countdown-bidding-closed">{countdownCompleteActive}</span>
            <Link href="/auctions/" className={styles.link}>
                <FormattedMessage
                    id="SharedAuctionCountdown.browse"
                    defaultMessage="Browse Current Auctions"
                />
            </Link>
        </>
    );

    if (isPreviewAuction && startDate) {
        return (
            <CountdownBox isAuctionSiderailRedesignVariant={isAuctionSiderailRedesignVariant}>
                {!isCountdownFinished ? (
                    <CountdownMessage
                        isPreviewAuction
                        isAuctionSiderailRedesignVariant={isAuctionSiderailRedesignVariant}
                        targetDate={startDate}
                        onCountdownFinished={onCountdownFinished}
                    />
                ) : (
                    <>{countdownCompletePreview}</>
                )}
            </CountdownBox>
        );
    } else if ((isActiveAuction || isTransitionAuction) && endDate) {
        return (
            <CountdownBox
                isAuctionSiderailRedesignVariant={isAuctionSiderailRedesignVariant}
                isAuctionFinished={!showActiveCountdown}
            >
                {showActiveCountdown ? (
                    <CountdownMessage
                        isAuctionSiderailRedesignVariant={isAuctionSiderailRedesignVariant}
                        targetDate={endDate}
                        onCountdownFinished={onCountdownFinished}
                    />
                ) : (
                    <>{isAuctionSiderailRedesignVariant ? biddingClosed : auctionEnded}</>
                )}
            </CountdownBox>
        );
    } else {
        return (
            <CountdownBox
                isAuctionSiderailRedesignVariant={isAuctionSiderailRedesignVariant}
                isAuctionFinished
            >
                {isAuctionSiderailRedesignVariant ? biddingClosed : auctionEnded}
            </CountdownBox>
        );
    }
};

export default PdpSharedAuctionCountdown;
